<script setup lang="ts">
import { onMounted, onBeforeUnmount } from "vue";
import { useProductPageStore } from "~/stores/main";

// -----------------------
// props
// -----------------------
const props = withDefaults(
  defineProps<{
    isInModal?: boolean;
    store?: any;
  }>(),
  {
    isInModal: false,
    store: null,
  },
);

// -----------------------
// composables
// -----------------------
const store = props.store || useProductPageStore();
const { t } = useI18n();

// -----------------------
// state for modal
// -----------------------
const isModalVisible = ref(false);
const modalContent = ref("");
const modalPosition = ref({
  top: "auto",
  left: "auto",
  right: "auto",
  bottom: "auto",
});

// Close modal on outside click
const handleClickOutside = (event: MouseEvent) => {
  const modal = document.querySelector(".product-modal");
  if (modal && !modal.contains(event.target as Node)) {
    hideModal();
  }
};

/*
const showModal = (event: MouseEvent) => {
  const span = (event.target as HTMLElement).closest("span[data-token]");
  if (span) {
    // Prevent event propagation to document
    event.stopPropagation();

    modalPosition.value = {
      top: "auto",
      left: "auto",
      right: "auto",
      bottom: "auto",
    };

    modalContent.value = span.getAttribute("data-token") || "";

    const spanRect = span.getBoundingClientRect();

    if (
      spanRect.left + window.scrollX + spanRect.width / 2 <
      window.innerWidth / 2
    ) {
      modalPosition.value = {
        top: spanRect.top + window.scrollY + 24 + "px",
        left: spanRect.left + window.scrollX + spanRect.width / 2 + "px",
        bottom: "auto",
        right: "auto",
      };
    } else {
      modalPosition.value = {
        top: spanRect.top + window.scrollY + 24 + "px",
        left:
          spanRect.left +
          window.scrollX +
          spanRect.width / 2 -
          window.innerWidth / 2 +
          "px",
        bottom: "auto",
        right: "auto",
      };
    }

    isModalVisible.value = true;
  }
};
*/
const showModal = (event: MouseEvent) => {
  const span = (event.target as HTMLElement).closest("span[data-token]");
  if (span) {
    event.stopPropagation();

    modalContent.value = span.getAttribute("data-token") || "";

    const spanRect = span.getBoundingClientRect();

    if (props.isInModal) {
      // When in a modal, position relative to the modal content
      const modalContent = document.querySelector(
        ".base-modal .m-3",
      ) as HTMLElement;
      if (modalContent) {
        const modalRect = modalContent.getBoundingClientRect();
        modalPosition.value = {
          top: `${spanRect.bottom - modalRect.top + 8}px`, // 8px margin below
          left: `${spanRect.left - modalRect.left}px`,
          right: "auto",
          bottom: "auto",
        };
      } else {
        // Fallback to document positioning
        modalPosition.value = {
          top: `${spanRect.bottom + window.scrollY + 8}px`,
          left: `${spanRect.left + window.scrollX}px`,
          right: "auto",
          bottom: "auto",
        };
      }
    } else {
      // Normal document positioning
      modalPosition.value = {
        top: `${spanRect.bottom + window.scrollY + 8}px`, // 8px margin below
        left: `${spanRect.left + window.scrollX}px`,
        right: "auto",
        bottom: "auto",
      };
    }

    isModalVisible.value = true;
  }
};

const hideModal = () => {
  isModalVisible.value = false;
  modalContent.value = "";
};

// Attach/detach outside click listener
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div
    class="col-span-full mt-2.5 lg:mt-[17px] lg:mt-[38px] xl:mt-[38px] text-brand-headline"
  >
    <h2
      class="font-bold font-lora !text-2xl lg:!text-3xl text-center h-[60px] !leading-[60px] !mt-0 mb-2 lg:mb-7"
    >
      {{ t("product_description") }}
    </h2>
    <!-- Render description -->
    <!-- eslint-disable vue/no-v-html -->
    <div
      class="mb-[31px] lg:mb-[45px]"
      @click="showModal"
      v-html="store.masterProduct.description"
    ></div>
    <!-- eslint-enable -->

    <!-- Modal -->
    <div
      v-if="isModalVisible"
      class="product-modal absolute bg-white p-4 rounded shadow-md border z-[1400]"
      :style="{
        top: `${modalPosition?.top}`,
        left: `${modalPosition?.left}`,
        bottom: `${modalPosition?.bottom}`,
        right: `${modalPosition?.right}`,
      }"
    >
      <button
        class="absolute top-2 right-2 text-gray-500 hover:text-black"
        @click="hideModal"
      >
        ✖
      </button>
      <!-- eslint-disable vue/no-v-html -->
      <div v-html="modalContent"></div>
      <!-- eslint-enable -->
    </div>
  </div>
</template>

<style scoped>
.product-modal {
  min-width: 250px;
  max-width: 400px;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
</style>
